const AI_NOTICE_ALERT_STORAGE_KEY = 'aiNoticeAlertLocks';
const AI_NOTICE_ALERT_EXPIRE_TIME = 5 * 60 * 1000;
const AI_NOTICE_ALERT_MAX_SIZE = 1000;

/**
 * 根据登录后动态菜单树判断是否拥有 AI 一级菜单权限。
 * 这里不依赖当前路由，避免页面切换导致提醒权限发生变化。
 *
 * @param {Array} authList 动态菜单树
 * @returns {Boolean} 是否存在 /ai 一级菜单
 */
export function hasAiNoticePermission(authList) {
  if (!Array.isArray(authList)) {
    return false;
  }
  return authList.some((item) => {
    if (!item) {
      return false;
    }
    if (item.path === '/ai') {
      return true;
    }
    return hasAiNoticePermission(item.children);
  });
}

/**
 * 生成 AI 提醒记录表中的渠道、酒店和通知组合键。
 *
 * @param {String} channel 提醒渠道
 * @param {String} hotelCode 酒店编码
 * @param {String} noticeNo AI 通知编号
 * @returns {String} 记录表键
 */
export function getAiNoticeAlertKey(channel, hotelCode, noticeNo) {
  return `${channel}:${hotelCode}:${noticeNo}`;
}

function getAiNoticeAlertStorage() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
}

function readAiNoticeAlertRecords(storage, now) {
  let records = {};
  try {
    records = JSON.parse(storage.getItem(AI_NOTICE_ALERT_STORAGE_KEY) || '{}');
  } catch (e) {
    records = {};
  }
  if (!records || Array.isArray(records) || typeof records !== 'object') {
    records = {};
  }
  Object.keys(records).forEach((key) => {
    if (Number(records[key]) <= now) {
      delete records[key];
    }
  });
  return records;
}

function writeAiNoticeAlertRecords(storage, records) {
  const keys = Object.keys(records)
    .sort((left, right) => Number(records[right]) - Number(records[left]))
    .slice(0, AI_NOTICE_ALERT_MAX_SIZE);
  const finalRecords = keys.reduce((result, key) => {
    result[key] = records[key];
    return result;
  }, {});
  if (keys.length) {
    storage.setItem(AI_NOTICE_ALERT_STORAGE_KEY, JSON.stringify(finalRecords));
  } else {
    storage.removeItem(AI_NOTICE_ALERT_STORAGE_KEY);
  }
}

function acquireAiNoticeAlertLockInStorage(channel, hotelCode, noticeNo) {
  const storage = getAiNoticeAlertStorage();
  if (!storage) {
    return false;
  }
  try {
    const now = Date.now();
    const key = getAiNoticeAlertKey(channel, hotelCode, noticeNo);
    const records = readAiNoticeAlertRecords(storage, now);
    if (Number(records[key]) > now) {
      writeAiNoticeAlertRecords(storage, records);
      return false;
    }
    records[key] = now + AI_NOTICE_ALERT_EXPIRE_TIME;
    writeAiNoticeAlertRecords(storage, records);
    return true;
  } catch (e) {
    return false;
  }
}

function releaseAiNoticeAlertLockInStorage(channel, hotelCode, noticeNo) {
  const storage = getAiNoticeAlertStorage();
  if (!storage) {
    return;
  }
  try {
    const records = readAiNoticeAlertRecords(storage, Date.now());
    delete records[getAiNoticeAlertKey(channel, hotelCode, noticeNo)];
    writeAiNoticeAlertRecords(storage, records);
  } catch (e) {
    return;
  }
}

function requestAiNoticeAlertLock(lockName, callback) {
  if (typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function') {
    return navigator.locks.request(lockName, callback);
  }
  // localStorage 的读写无法跨标签页原子化；旧浏览器仅能尽力降低极端并发重复。
  return Promise.resolve().then(callback);
}

/**
 * 异步领取 AI 渠道提醒资格。支持 navigator.locks 的浏览器会在锁内检查并写入记录。
 *
 * @param {String} channel 提醒渠道
 * @param {String} hotelCode 酒店编码
 * @param {String} noticeNo AI 通知编号
 * @returns {Promise<Boolean>} 当前标签页是否可以执行提醒
 */
export function acquireAiNoticeAlertLock(channel, hotelCode, noticeNo) {
  if (!channel || !hotelCode || !noticeNo) {
    return Promise.resolve(false);
  }
  const key = getAiNoticeAlertKey(channel, hotelCode, noticeNo);
  return requestAiNoticeAlertLock(`aiNoticeAlert:${key}`, () => acquireAiNoticeAlertLockInStorage(channel, hotelCode, noticeNo));
}

/**
 * 仅释放当前 AI 渠道、酒店和通知的提醒记录。
 *
 * @param {String} channel 提醒渠道
 * @param {String} hotelCode 酒店编码
 * @param {String} noticeNo AI 通知编号
 * @returns {Promise<void>} 释放完成信号
 */
export function releaseAiNoticeAlertLock(channel, hotelCode, noticeNo) {
  if (!channel || !hotelCode || !noticeNo) {
    return Promise.resolve();
  }
  const key = getAiNoticeAlertKey(channel, hotelCode, noticeNo);
  return requestAiNoticeAlertLock(`aiNoticeAlert:${key}`, () => releaseAiNoticeAlertLockInStorage(channel, hotelCode, noticeNo));
}
